import { SkipsProcessingJobToProcess } from './SkipsProcessingJobToProcess';

/**
 * Manages mission activation and state for the Skips Processing task
 * Simplified compared to BoundaryApprovalMissionManager - no unsaved edits tracking
 */
export class SkipsProcessingMissionManager {
  private jobs: Map<string, SkipsProcessingJobToProcess> = new Map();
  private activeMissionId: string | null = null;

  /**
   * Sets the list of jobs to manage
   */
  setJobs(jobs: SkipsProcessingJobToProcess[]): void {
    this.jobs.clear();
    jobs.forEach((job) => this.jobs.set(job.id, job));
  }

  /**
   * Gets all jobs
   */
  getJobs(): SkipsProcessingJobToProcess[] {
    return Array.from(this.jobs.values());
  }

  /**
   * Gets a specific job by ID
   */
  getJob(jobId: string): SkipsProcessingJobToProcess | undefined {
    return this.jobs.get(jobId);
  }

  /**
   * Gets the currently active job
   */
  getActiveJob(): SkipsProcessingJobToProcess | undefined {
    if (!this.activeMissionId) {
      return undefined;
    }

    return this.jobs.get(this.activeMissionId);
  }

  /**
   * Activates a job/mission
   * @param jobId The job ID to activate
   * @returns Object with success status and optional message
   */
  async activateJob(jobId: string): Promise<{ success: boolean; message?: string }> {
    const job = this.jobs.get(jobId);

    if (!job) {
      return { success: false, message: 'Job not found' };
    }

    // Check if there's already an active job
    const currentActiveJob = this.getActiveJob();

    if (currentActiveJob && currentActiveJob.id !== jobId) {
      // Deactivate the current job
      currentActiveJob.setActive(false);
    }

    // Activate the new job
    job.setActive(true);
    this.activeMissionId = jobId;

    return { success: true };
  }

  /**
   * Deactivates the current job
   */
  deactivateCurrentJob(): void {
    const currentJob = this.getActiveJob();

    if (currentJob) {
      currentJob.setActive(false);
      this.activeMissionId = null;
    }
  }

  /**
   * Resets all state
   */
  reset(): void {
    this.jobs.clear();
    this.activeMissionId = null;
  }
}
